/**
 * Skills Hub: typed client over the local `api` helper (M2.4).
 *
 * Mirrors the M2.2 REST surface (every response is the `{data,error,meta}`
 * envelope). Shared DTOs come from `@tabbio/types`; do NOT use
 * `@tabbio/api-client` (dead). Run/entitlement/confirmation errors surface as a
 * typed {@link SkillsApiError} so callers can branch on `code`.
 */
import { trackProductEvent } from "./product-analytics";
import type {
  APIResponse,
  CvSectionId,
  EntitlementDecision,
  SkillAccessPreference,
  SkillAccessWhoCanRun,
  SkillCvConfig,
  SkillDetail,
  SkillGenerateDraftResult,
  SkillKind,
  SkillInstallationResult,
  SkillListQuery,
  SkillListResult,
  SkillPrepareRunResult,
  SkillRunConfig,
  SkillRecommendedModel,
  SkillRunDTO,
  SkillRunResult,
  SkillShelf,
  SkillStatus,
  SkillSummary,
  SkillVisibility,
} from "@tabbio/types";
import { api, readAPIResponse } from "./api";
import { getAPIBaseURL } from "./api-url";
import { appFetch } from "./app-auth";

// ── Error codes the run flow branches on ──────────────────────────────────────

export const SKILL_ENTITLEMENT_DENIED = "SUBSCRIPTION_ENTITLEMENT_DENIED";
export const SKILL_CONFIRMATION_REQUIRED = "SKILL_CONFIRMATION_REQUIRED";
/** The CV changed after the run was reviewed: nothing was written (B09). */
export const SKILL_CONFIRMATION_STALE = "SKILL_CONFIRMATION_STALE";
/** The run would write differently than reviewed: nothing was written (B09). */
export const SKILL_CONFIRMATION_MISMATCH = "SKILL_CONFIRMATION_MISMATCH";
export const SKILL_BLOCKED = "SKILL_BLOCKED";

/** Entitlement decision attached to a 403 SUBSCRIPTION_ENTITLEMENT_DENIED. */
export type SkillEntitlementDecision = EntitlementDecision;

export class SkillsApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "SkillsApiError";
  }

  get decision(): SkillEntitlementDecision | undefined {
    return this.meta?.decision as SkillEntitlementDecision | undefined;
  }
}

export function isEntitlementDenied(error: unknown): error is SkillsApiError {
  return (
    error instanceof SkillsApiError && error.code === SKILL_ENTITLEMENT_DENIED
  );
}

export function isConfirmationRequired(
  error: unknown,
): error is SkillsApiError {
  return (
    error instanceof SkillsApiError &&
    error.code === SKILL_CONFIRMATION_REQUIRED
  );
}

/**
 * The confirmation no longer describes the run (the CV changed, or another
 * write mode): nothing was written and no credit was used. Running again
 * prepares a new confirmation for the CV as it is now.
 */
export function isConfirmationOutdated(
  error: unknown,
): error is SkillsApiError {
  return (
    error instanceof SkillsApiError &&
    (error.code === SKILL_CONFIRMATION_STALE ||
      error.code === SKILL_CONFIRMATION_MISMATCH)
  );
}

export function isSkillBlocked(error: unknown): error is SkillsApiError {
  return error instanceof SkillsApiError && error.code === SKILL_BLOCKED;
}

function unwrap<T>(res: APIResponse<T>): T {
  if (res.error) {
    throw new SkillsApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
  if (res.data == null) {
    throw new SkillsApiError("Empty response", "EMPTY_RESPONSE");
  }
  return res.data;
}

// ── Two-way skills: parsed manifest config on the detail DTO ─────────────────

/**
 * kind/run/cv_config parsed from the SKILL.md frontmatter, exposed by
 * GET /api/skills/:skillId (docs/skills-two-way-design.md). Kept as a local
 * extension until `SkillDetail` in @tabbio/types carries them natively.
 */
export interface SkillDetailConfigExtras {
  kind?: SkillKind | null;
  run?: SkillRunConfig | null;
  cvConfig?: SkillCvConfig | null;
}

export type SkillDetailWithConfig = SkillDetail & SkillDetailConfigExtras;

// ── Hub list / detail ─────────────────────────────────────────────────────────

export async function listSkills(
  query: SkillListQuery = {},
): Promise<SkillListResult> {
  const params = new URLSearchParams();
  if (query.shelf) params.set("shelf", String(query.shelf));
  if (query.category) params.set("category", query.category);
  if (query.locale) params.set("locale", query.locale);
  if (query.tier) params.set("tier", String(query.tier));
  if (query.type) params.set("type", String(query.type));
  if (query.region) params.set("region", query.region);
  if (query.q) params.set("q", query.q);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit) params.set("limit", String(query.limit));
  const qs = params.toString();
  const res = await api.get<SkillListResult>(
    `/api/skills${qs ? `?${qs}` : ""}`,
  );
  return unwrap(res);
}

export async function getSkill(
  skillId: string,
): Promise<SkillDetailWithConfig> {
  return unwrap(await api.get<SkillDetailWithConfig>(`/api/skills/${skillId}`));
}

/** The skill's SKILL.md markdown (for the detail view). */
export async function getSkillContent(skillId: string): Promise<string> {
  const res = await api.get<{ markdown: string }>(
    `/api/skills/${skillId}/content`,
  );
  return unwrap(res).markdown ?? "";
}

export async function installSkill(
  skillId: string,
): Promise<SkillInstallationResult> {
  const result = unwrap(
    await api.post<SkillInstallationResult>(
      `/api/skills/${skillId}/install`,
      {},
    ),
  );
  // Marketplace acquisition; skill_applied only measures runs.
  trackProductEvent("skill_installed", { skill_id: skillId });
  return result;
}

export async function uninstallSkill(
  skillId: string,
): Promise<SkillInstallationResult> {
  const result = unwrap(
    await api.delete<SkillInstallationResult>(`/api/skills/${skillId}/install`),
  );
  trackProductEvent("skill_uninstalled", { skill_id: skillId });
  return result;
}

// ── Run flow ────────────────────────────────────────────────────────────────

export interface PrepareSkillRunInput {
  cvId: string;
  inputs?: Record<string, unknown>;
  /** The write the run will make; the confirmation is bound to it (B09). */
  mode?: "update" | "new_version";
  /** Title of the copy a new_version run creates. */
  title?: string;
}

export async function prepareSkillRun(
  skillId: string,
  input: PrepareSkillRunInput,
): Promise<SkillPrepareRunResult> {
  return unwrap(
    await api.post<SkillPrepareRunResult>(
      `/api/skills/${skillId}/prepare-run`,
      input,
    ),
  );
}

/**
 * The confirmation for exactly the write `run` makes (B09): same CV, same
 * mode (`update` when the run sends none, as the server defaults) and
 * the copy's title. A confirmation for another mode is refused by the run.
 */
export function prepareSkillRunFor(
  skillId: string,
  run: RunSkillInput & { cvId: string },
): Promise<SkillPrepareRunResult> {
  return prepareSkillRun(skillId, {
    cvId: run.cvId,
    inputs: run.inputs,
    mode: run.mode ?? "update",
    ...(run.title ? { title: run.title } : {}),
  });
}

export interface RunSkillInput {
  cvId?: string;
  inputs?: Record<string, unknown>;
  idempotencyKey?: string;
  dryRun?: boolean;
  confirmationToken?: string;
  /** Full-CV writes require a confirmation token; section runs do not. */
  scope?: "section" | "full_cv";
  /** CV skills: 'update' overwrites the source CV; 'new_version' creates a named copy. */
  mode?: "update" | "new_version";
  /** Title for the CV a run produces (name the new version). */
  title?: string;
}

export async function runSkill(
  skillId: string,
  input: RunSkillInput = {},
): Promise<SkillRunResult> {
  return unwrap(
    await api.post<SkillRunResult>(`/api/skills/${skillId}/runs`, input),
  );
}

export async function getSkillRun(runId: string): Promise<SkillRunDTO> {
  return unwrap(await api.get<SkillRunDTO>(`/api/skills/runs/${runId}`));
}

export async function getSkillRunByIdempotencyKey(
  idempotencyKey: string,
): Promise<SkillRunDTO> {
  return unwrap(
    await api.get<SkillRunDTO>(
      `/api/skills/runs/by-idempotency/${encodeURIComponent(idempotencyKey)}`,
    ),
  );
}

export interface UndoSkillRunResult {
  runId: string;
  reverted: boolean;
  cvId?: string | null;
  // CV spine B05 (optional: older API builds send none of these).
  /** The CV revision the undo produced; null when it deleted a copy. */
  revision?: number | null;
  /** The parts put back (`summary`, `experience`, ...). */
  fields?: string[];
  /** True when "Restore anyway" replaced edits made after the run. */
  restoredAnyway?: boolean;
  /** True when the undo deleted the copy the run made. */
  deleted?: boolean;
  /** True when the run was already undone: nothing changed this time. */
  alreadyReverted?: boolean;
}

/**
 * Undo a skill run. The API refuses (SKILL_UNDO_CONFLICT) when the user
 * edited parts the run changed; `force` ("Restore anyway") reverts them all
 * the same. Use `undoSkillRunWithConflictPrompt` (skill-undo-flow) from UI.
 */
export async function undoSkillRun(
  runId: string,
  options: { force?: boolean } = {},
): Promise<UndoSkillRunResult> {
  const result = unwrap(
    await api.post<UndoSkillRunResult>(
      `/api/skills/runs/${runId}/undo`,
      options.force ? { force: true } : {},
    ),
  );
  // Undo is the sharpest quality score a skill's output gets.
  trackProductEvent("skill_run_undone", {
    reverted: result.reverted,
    restoredAnyway: result.restoredAnyway === true,
  });
  return result;
}

// ── Create / edit / delete / export ──────────────────────────────────────────

export interface CreateSkillInput {
  /** Full SKILL.md markdown: or the structured fields below. */
  markdown?: string;
  name?: string;
  description?: string;
  nameAr?: string;
  descriptionAr?: string;
  /** The method / how the skill should behave (manifest body). */
  instructions?: string;
  categories?: string[];
  tags?: string[];
  locales?: string[];
  region?: string;
  /** Preset icon id (flag | doc | sparkle | briefcase | grad | star). */
  iconKey?: string;
  /** Public CDN URL for an uploaded custom icon; '' clears it. */
  iconUrl?: string;
  /** Per-skill tool allow-list. */
  scopes?: string[];
  /** Default model for runs; persisted as `Skill.recommendedModel`. */
  recommendedModel?: SkillRecommendedModel;
  /** Two-way skills discriminator: absent/'default' = portable skill. */
  kind?: SkillKind;
  /** Run-panel spec written into the manifest `run` block. */
  run?: SkillRunConfig;
  /** `kind: cv_custom` only: manifest `cv_config` block. */
  cvConfig?: SkillCvConfig;
}

export interface GenerateSkillDraftInput {
  prompt: string;
  kind?: SkillKind;
  /**
   * The catalog id, NOT the picker's display id. The server validates this
   * against `lite|flash|gpt|sonnet|gpro|opus`, so passing 'gemini-flash' 400s before
   * the request reaches the service. Use `toCatalogModel()` to convert.
   */
  model?: SkillRecommendedModel;
  locale?: string;
}

export async function generateSkillDraft(
  input: GenerateSkillDraftInput,
): Promise<SkillGenerateDraftResult> {
  return unwrap(
    await api.post<SkillGenerateDraftResult>("/api/skills/generate", input),
  );
}

export interface GenerateSkillSectionPromptInput {
  sectionId: CvSectionId;
  skillName?: string;
  skillPrompt?: string;
  /** Present means "regenerate, differently" rather than "draft one". */
  currentPrompt?: string;
  otherSections?: CvSectionId[];
  locale?: string;
}

/** Drafts one CV section's authoring instruction for the builder. */
export async function generateSkillSectionPrompt(
  input: GenerateSkillSectionPromptInput,
): Promise<{ sectionId: CvSectionId; prompt: string }> {
  return unwrap(
    await api.post<{ sectionId: CvSectionId; prompt: string }>(
      "/api/skills/generate-section-prompt",
      input,
    ),
  );
}

export async function createSkill(
  input: CreateSkillInput,
): Promise<SkillDetail> {
  return unwrap(await api.post<SkillDetail>("/api/skills", input));
}

/** PATCH: cuts a new active SkillVersion from markdown or structured fields. */
export type UpdateSkillInput = CreateSkillInput;

export async function updateSkill(
  skillId: string,
  input: UpdateSkillInput,
): Promise<SkillDetail> {
  return unwrap(await api.patch<SkillDetail>(`/api/skills/${skillId}`, input));
}

/** Soft-archives an owned skill (hidden from lists). */
export async function deleteSkill(skillId: string): Promise<void> {
  const res = await api.delete<unknown>(`/api/skills/${skillId}`);
  if (res.error) {
    throw new SkillsApiError(
      res.error.message,
      res.error.code,
      res.meta as Record<string, unknown> | undefined,
    );
  }
}

/**
 * GET /api/skills/:skillId/export: the full round-trippable SKILL.md.
 * The endpoint responds with `text/markdown`; a `{data:{markdown}}` JSON
 * envelope is tolerated as a fallback so either server shape works.
 */
export async function exportSkillMarkdown(skillId: string): Promise<string> {
  const response = await appFetch(
    `${getAPIBaseURL()}/api/skills/${skillId}/export`,
    { method: "GET" },
    { requiresAuth: true },
  );
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("json")) {
    const parsed = await readAPIResponse<{ markdown?: string }>(response);
    return unwrap(parsed).markdown ?? "";
  }
  if (!response.ok) {
    throw new SkillsApiError(
      `Export failed (${response.status})`,
      `HTTP_${response.status}`,
    );
  }
  return response.text();
}

/**
 * What POST /api/skills/:skillId/publish and .../unpublish return.
 *
 * Publish moves an owned skill to `visibility: public`, `shelf: community`,
 * `status: published` and answers with the canonical share link; unpublish is
 * the exact inverse (`private` / `own` / `private`) and answers with
 * `shareUrl: null`, since there is no public page left to link to.
 */
export interface SkillPublishResult {
  skillId: string;
  status: SkillStatus | string;
  visibility: SkillVisibility | string;
  shelf: SkillShelf | string;
  /** Canonical `skills.<host>/<skillId>` link, or null once unpublished. */
  shareUrl: string | null;
}

export async function publishSkill(
  skillId: string,
): Promise<SkillPublishResult> {
  return unwrap(
    await api.post<SkillPublishResult>(`/api/skills/${skillId}/publish`, {}),
  );
}

/** Takes an owned skill back off the community shelf (the share link 404s). */
export async function unpublishSkill(
  skillId: string,
): Promise<SkillPublishResult> {
  return unwrap(
    await api.post<SkillPublishResult>(`/api/skills/${skillId}/unpublish`, {}),
  );
}

// ── Settings (Agent Access) ────────────────────────────────────────────────────

export async function getSkillSettings(): Promise<SkillAccessPreference> {
  return unwrap(await api.get<SkillAccessPreference>("/api/skills/settings"));
}

export interface UpdateSkillSettingsInput {
  whoCanRunSkills?: SkillAccessWhoCanRun;
  communitySkillsEnabled?: boolean;
  confirmFullCvWrite?: boolean;
  blockedSkillIds?: string[];
  blockedCreatorIds?: string[];
}

export async function updateSkillSettings(
  patch: UpdateSkillSettingsInput,
): Promise<SkillAccessPreference> {
  return unwrap(
    await api.put<SkillAccessPreference>("/api/skills/settings", patch),
  );
}

// ── Run log ───────────────────────────────────────────────────────────────────

export interface SkillRunLogResult {
  runs: SkillRunDTO[];
  nextCursor?: string | null;
}

export async function listSkillRuns(
  params: {
    limit?: number;
    cursor?: string;
  } = {},
): Promise<SkillRunLogResult> {
  const search = new URLSearchParams();
  if (params.limit) search.set("limit", String(params.limit));
  if (params.cursor) search.set("cursor", params.cursor);
  const qs = search.toString();
  return unwrap(
    await api.get<SkillRunLogResult>(`/api/skills/runs${qs ? `?${qs}` : ""}`),
  );
}

// ── Display helpers (shelf re-export not needed; values from @tabbio/types) ────

export type { SkillSummary, SkillDetail, SkillRunDTO, SkillAccessPreference };
